import { useMemo, useState } from "react";
import { ERA_1_POOL, type EraPoolDef } from "@reagent/content";
import { marketFor } from "@reagent/meta";
import { useGameStore } from "../../app/gameStore";
import { useRunStore } from "../../app/runStore";
import { getCardView } from "../cards/cardViews";
import "./pathway-pick.css";

/**
 * The start of a run: choose two production pathways from the era's pool.
 *
 * Picks are ordered — the first is stage 1 — so the screen tracks the order
 * they were clicked in rather than a set. Clicking a chosen pathway drops it,
 * and the remaining pick keeps its place.
 */
export function PathwayPick({ pool = ERA_1_POOL }: { pool?: EraPoolDef }) {
  const run = useRunStore((s) => s.run);
  const pickPathways = useRunStore((s) => s.pickPathways);
  const loadRunStage = useGameStore((s) => s.loadRunStage);
  const [picks, setPicks] = useState<readonly string[]>([]);

  /* Compute the market from the run seed, memoized on seed and pool. Market
     never changes within a run, and the seed is stable across renders. */
  const market = useMemo(() => marketFor(run.seed, pool), [run.seed, pool]);

  if (run.status !== "picking") return null;

  const toggle = (id: string) => {
    setPicks((current) => {
      if (current.includes(id)) return current.filter((p) => p !== id);
      // A third click replaces the partner, keeping stage 1 in place.
      return current.length >= 2 ? [current[0]!, id] : [...current, id];
    });
  };

  const ready = picks.length === 2;
  const confirm = () => {
    if (!ready) return;
    pickPathways(picks[0]!, picks[1]!, pool);
    // The component stays mounted, so a later run must not open pre-picked.
    setPicks([]);
    loadRunStage();
  };

  return (
    <div className="pathway-pick">
      <div className="pathway-pick__panel">
        <header className="pathway-pick__head">
          <span className="pathway-pick__eyebrow">production pathways</span>
          <h1 className="pathway-pick__title">Choose two pathways</h1>
          <p className="pathway-pick__hint">
            Pick two. The first you choose is stage 1; the second is its partner. Click a chosen
            pathway again to undo it.
          </p>
        </header>

        {/* Show the market (commodities' price tiers and grades) before the
            pathway list. Only render when the pool has commodities; regional
            modifiers are shown when non-null. */}
        {pool.commodities.length > 0 && (
          <div className="pathway-pick__market">
            <h2 className="pathway-pick__market-title">market</h2>
            <ul className="pathway-pick__market-list">
              {market.commodities.map((commodity) => (
                <li key={commodity.commodity} className="pathway-pick__market-item">
                  <span className="pathway-pick__market-name">{commodity.commodity}</span>
                  <span className="pathway-pick__market-tier">
                    {commodity.price} price
                  </span>
                  <span className="pathway-pick__market-grade">
                    {commodity.grade} supply
                  </span>
                </li>
              ))}
            </ul>
            {/* Show the regional modifier only when present (always null today). */}
            {market.regionalModifier && (
              <p className="pathway-pick__market-modifier">
                regional: {market.regionalModifier}
              </p>
            )}
          </div>
        )}

        {pool.pathways.length === 0 ? (
          <p className="pathway-pick__empty">No pathways are on offer.</p>
        ) : (
          <ul className="pathway-pick__list">
            {pool.pathways.map((pathway) => {
              const order = picks.indexOf(pathway.id);
              const chosen = order >= 0;
              const imports = [
                ...pathway.core.imports.map((token) => ({ token, tier: "core" })),
                ...pathway.refined.imports.map((token) => ({ token, tier: "refined" })),
              ];
              return (
                <li key={pathway.id}>
                  <button
                    className={`pathway-pick__entry${chosen ? " is-chosen" : ""}`}
                    aria-pressed={chosen}
                    onClick={() => toggle(pathway.id)}
                  >
                    <span className="pathway-pick__entry-head">
                      <span className="pathway-pick__name">{pathway.name}</span>
                      {/* Words, not just colour, say which pick this is. */}
                      <span className="pathway-pick__badge">
                        {chosen ? (order === 0 ? "stage 1" : "partner") : ""}
                      </span>
                    </span>
                    <dl className="pathway-pick__facts">
                      <dt>exports</dt>
                      <dd>{pathway.exports.join(", ") || "none"}</dd>
                      <dt>imports</dt>
                      <dd>
                        {imports.length === 0
                          ? "none"
                          : imports.map(({ token, tier }) => (
                              <span key={`${tier}-${token}`}>
                                {token} <span className="pathway-pick__tier">{tier}</span>
                              </span>
                            ))}
                      </dd>
                      <dt>package</dt>
                      <dd>
                        {pathway.package.map((cardId) => (
                          <span key={cardId} className="pathway-pick__card">
                            {getCardView(cardId)?.name ?? cardId}
                          </span>
                        ))}
                      </dd>
                    </dl>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <footer className="pathway-pick__foot">
          <span className="pathway-pick__count">{picks.length} of 2 chosen</span>
          <button className="pathway-pick__go" disabled={!ready} onClick={confirm}>
            confirm pathways
          </button>
        </footer>
      </div>
    </div>
  );
}
